import { dateFormatOptions } from '../../../Common/PageBuilder/models/date-format-options.const';
import { BriefingStyleStateEnum } from '../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { MonitoringTextStyleModel } from './monitoring-text-style.model';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { PaddingModel } from '../../../Common/PageBuilder/models/padding.model';
import { DefaultFontsEnum } from '../../../Common/PageBuilder/enums/default-fonts.enum';

export class MonitoringDateStyleModel extends GenericStyleModel {
    backgroundColor: string;
    font: MonitoringTextStyleModel;
    dateFormat: string;
    declare styleState: number | string;
    declare displayStyleState: number | string;

    showTimeZone: boolean;
    showTime: boolean;
    timezone: string | null;
    timeFormat: string | null;

    // These styles are saved on the block level only, not saved styles
    showDate: boolean;
    showViewInBrowser: boolean = true;
    viewInBrowserLabel: string;
    padding: PaddingModel;
    locale: string;

    constructor(apiModel?: any) {
        super();
        if (apiModel) {
            this.setFromApiModel(apiModel);
        } else {
            this.font = new MonitoringTextStyleModel();
            this.font.color = '#292929';
        }

        if (!this.backgroundColor) {
            this.backgroundColor = '#f7f9fd';
        }
        if (!this.dateFormat) {
            this.dateFormat = 'F d, Y';
        }
        if (!this.styleState || this.styleState === StyleStateEnum.DEFAULT) {
            this.styleState = BriefingStyleStateEnum.BRIEFING_1;
        }
        if (!this.displayStyleState || this.displayStyleState === StyleStateEnum.DEFAULT) {
            this.displayStyleState = this.styleState;
        }
        if (
            (
                !this.defaultReference ||
                this.defaultReference === StyleStateEnum.DEFAULT
            ) &&
            typeof this.styleState === 'string'
        ) {
            // Legacy data may not have default reference set, if the styleState is custom and there's no default reference, the default reference
            // should be BRIEFING_1 because it must have been created back when there was only one default
            this.defaultReference = this.styleState !== StyleStateEnum.CUSTOM ? this.styleState as string : BriefingStyleStateEnum.BRIEFING_1;
        }
        if (this.showDate === null || this.showDate === undefined) {
            this.showDate = true;
        }
        if (this.showViewInBrowser === null || this.showViewInBrowser === undefined) {
            this.showViewInBrowser = false;
        }
        if (this.viewInBrowserLabel === null || this.viewInBrowserLabel === undefined) {
            this.viewInBrowserLabel = 'View in Browser';
        }

        if (!this.padding){
            this.padding = new PaddingModel();
        }
    }

    clone(base?: MonitoringDateStyleModel): MonitoringDateStyleModel {
        const apiModel: any = {
            backgroundColor: this.backgroundColor,
            font: this.font,
            dateFormat: this.dateFormat,
            styleState: this.styleState,
            displayStyleState: this.displayStyleState,
            showDate: this.showDate,
            showViewInBrowser: this.showViewInBrowser,
            viewInBrowserLabel: this.viewInBrowserLabel,
            defaultReference: this.defaultReference,
            padding: this.padding,
            locale: this.locale,
            showTimeZone: this.showTimeZone,
            showTime: this.showTime,
            timezone: this.timezone,
            timeFormat: this.timeFormat
        };

        if (base) {
            if (base.showDate) {
                apiModel.showDate = base.showDate;
            }
            if (base.showViewInBrowser) {
                apiModel.showViewInBrowser = base.showViewInBrowser;
            }
            if (base.viewInBrowserLabel) {
                apiModel.viewInBrowserLabel = base.viewInBrowserLabel;
            }
        }

        const clone = new MonitoringDateStyleModel(apiModel);

        if (this.template) {
            clone.template = this.template.clone();
        }

        return clone;
    }

    public equals(other: MonitoringDateStyleModel, compareTemplate: boolean = true): boolean {
        if (this.backgroundColor !== other.backgroundColor) {
            return false;
        }
        if (this.dateFormat !== other.dateFormat) {
            return false;
        }
        if (this.styleState !== other.styleState) {
            return false;
        }
        if (!this.font.equals(other.font, false)) {
            return false;
        }
        if (this.showDate !== other.showDate) {
            return false;
        }
        if (this.showViewInBrowser !== other.showViewInBrowser) {
            return false;
        }
        if (this.viewInBrowserLabel !== other.viewInBrowserLabel) {
            return false;
        }
        if (compareTemplate && this.template && !this.template.equals(other.template)) {
            return false;
        }

        if (!this.padding.equals(other.padding)) {
            return false;
        }

        if (this.locale !== other.locale) {
            return false;
        }

        if (this.showTimeZone !== other.showTimeZone) {
            return false;
        }

        if (this.showTime !== other.showTime) {
            return false;
        }

        if (this.timezone !== other.timezone) {
            return false;
        }

        if (this.timeFormat !== other.timeFormat) {
            return false;
        }

        return true;
    }

    setFromApiModel(apiModel: any): void {
        super.setFromApiModel(apiModel);

        if (typeof apiModel === 'string') {
            apiModel = JSON.parse(apiModel);
        }

        if (apiModel.hasOwnProperty('font') && apiModel.font) {
            this.font = new MonitoringTextStyleModel(apiModel.font);
        } else {
            this.font = new MonitoringTextStyleModel();

            // Legacy format had separate fields that we need to check for.
            if (apiModel.hasOwnProperty('textColor')) {
                this.font.color = apiModel.textColor;
            } else {
                this.font.color = '#292929';
            }

            if (apiModel.hasOwnProperty('fontFamily')) {

                // According to https://phabricator.agilitypr.internal/D16745
                // At some point bad values were added for Arial and Helvetica,
                // these should remain until we're confident no more active briefings are using the old value
                if (apiModel.fontFamily === DefaultFontsEnum.backwardCompatibilityArial) {
                    apiModel.fontFamily = DefaultFontsEnum.Arial;
                }
                if (apiModel.fontFamily === DefaultFontsEnum.backwardCompatibilityHelvetica) {
                    apiModel.fontFamily = DefaultFontsEnum.Helvetica;
                }

                this.font.fontFamily = apiModel.fontFamily;
            }
        }
        if (!this.font.fontSize) {
            this.font.fontSize = '14px';
        }
        if (apiModel.hasOwnProperty('backgroundColor')) {
            this.backgroundColor = apiModel.backgroundColor;
        }
        if (apiModel.hasOwnProperty('dateFormat')) {
            this.dateFormat = apiModel.dateFormat;
        }
        if (apiModel.hasOwnProperty('showDate')) {
            this.showDate = apiModel.showDate;
        }
        if (apiModel.hasOwnProperty('showViewInBrowser')) {
            this.showViewInBrowser = apiModel.showViewInBrowser;
        }
        if (apiModel.hasOwnProperty('viewInBrowserLabel')) {
            this.viewInBrowserLabel = apiModel.viewInBrowserLabel;
        }
        if (apiModel.hasOwnProperty('template')) {
            this.template = apiModel.template;
        }
        if (apiModel.hasOwnProperty('padding')) {
            this.padding = new PaddingModel(apiModel.padding.paddingAll, apiModel.padding.paddingOption, apiModel.padding.topPadding,
                apiModel.padding.leftPadding, apiModel.padding.bottomPadding, apiModel.padding.rightPadding);
        }
        if (apiModel.hasOwnProperty('locale')) {
            this.locale = apiModel.locale;
        }
        this.showTimeZone = apiModel.hasOwnProperty('showTimeZone') ? apiModel.showTimeZone : false;
        this.showTime = apiModel.hasOwnProperty('showTime') ? apiModel.showTime : false;
        this.timezone = apiModel.hasOwnProperty('timezone') ? apiModel.timezone : null;
        this.timeFormat = apiModel.hasOwnProperty('timeFormat') ? apiModel.timeFormat : null;
    }

    toApi(): any {
        return {
            font: this.font,
            backgroundColor: this.backgroundColor,
            dateFormat: this.dateFormat,
            defaultReference: this.defaultReference ? this.defaultReference : 'BRIEFING_1',
            padding: this.padding,
            locale: this.locale,
            showTimeZone: this.showTimeZone,
            showTime: this.showTime,
            timezone: this.timezone,
            timeFormat: this.timeFormat
        };
    }

    toBlockStyle(): any {
        const blockStyle: any = {
            styleState: this.styleState,
            displayStyleState: this.displayStyleState,
            showDate: this.showDate,
            showViewInBrowser: this.showViewInBrowser,
            viewInBrowserLabel: this.viewInBrowserLabel,
            padding: this.padding
        };

        if (!this.isSavedStyle()) {
            return {
                ...blockStyle,
                font: this.font,
                backgroundColor: this.backgroundColor,
                dateFormat: this.dateFormat,
                locale: this.locale,
                showTimeZone: this.showTimeZone,
                showTime: this.showTime,
                timezone: this.timezone,
                timeFormat: this.timeFormat
            };
        }

        return blockStyle;
    }
}
